import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  #cartFetchId = 0;

  /** @type {AbortController | null} */
  #cartFetchController = null;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.#cartFetchController?.abort();
    this.#cartFetchController = null;
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    const eventItemCount = event.detail.data?.itemCount;
    const fetchId = ++this.#cartFetchId;

    this.#cartFetchController?.abort();
    this.#cartFetchController = new AbortController();

    try {
      const response = await fetch('/cart.js', { signal: this.#cartFetchController.signal });
      if (fetchId !== this.#cartFetchId) return;

      if (response.ok) {
        const cart = await response.json();
        if (fetchId !== this.#cartFetchId) return;

        const actualCount = Number(cart?.item_count) || 0;
        this.renderCartBubble(actualCount, false, comingFromProductForm);
        return;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // Fallback below if cart fetch fails.
    }

    if (fetchId !== this.#cartFetchId) return;
    if (eventItemCount == null) return;

    const fallbackCount = comingFromProductForm
      ? this.currentCartCount + (Number(eventItemCount) || 0)
      : Number(eventItemCount) || 0;

    this.renderCartBubble(fallbackCount, false, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, _comingFromProductForm, animate = true) => {
    const cartCount = Math.max(0, Number(itemCount) || 0);

    this.refs.cartBubbleCount.classList.toggle('hidden', cartCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', cartCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', cartCount > 0 && animate);

    this.currentCartCount = cartCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', cartCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
